import { useEffect, useLayoutEffect, useRef } from 'react'
import { useLocation, useNavigationType } from 'react-router-dom'
import { recordHistoryEntry } from './historyStack'

/**
 * Remembered window scroll offsets, keyed by react-router history entry.
 *
 * A module-level Map (not state) so positions survive component remounts and
 * the entries stay tied to a single browser session's history keys.
 */
const scrollPositions = new Map<string, number>()

/**
 * Restores the window scroll position when navigating back/forward (POP) and
 * scrolls to the top on a fresh navigation (PUSH, or a REPLACE that lands on
 * another route) — the behaviour the browser gives on a full page load, which
 * client-side routing otherwise loses.
 *
 * A REPLACE that stays on the same route is not a navigation but a state
 * update: list pages persist their filters and pagination in `location.state`
 * that way (see useListState), and react-router hands every replace a fresh
 * `location.key`. The viewport is left alone and the remembered offset — or a
 * restore still in progress — is carried over to the new key. Without that,
 * the save a list page does right after remounting cancelled the restore and
 * dropped you back at the top.
 *
 * Mount this once in the app shell (see Main.tsx). It also feeds the history
 * stack mirror behind useBackToList.
 *
 * The window/body is the scroll container (AdminLTE default layout); there is
 * no inner overflow element to track.
 */
export function useScrollRestoration(): void {
  const location = useLocation()
  const navigationType = useNavigationType()

  // Key and pathname of the history entry currently on screen. Kept in refs
  // so the single persistent scroll listener always writes to the active
  // entry, and so a REPLACE can be told apart from a route change.
  const activeKeyRef = useRef(location.key)
  const activePathRef = useRef<string | null>(null)

  // While we programmatically restore, suppress the scroll listener so the
  // restore itself doesn't overwrite a remembered position with an in-between
  // value.
  const restoringRef = useRef(false)
  const rafRef = useRef(0)

  useEffect(() => {
    const handleScroll = () => {
      if (!restoringRef.current) {
        scrollPositions.set(activeKeyRef.current, window.scrollY)
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', handleScroll)
      cancelAnimationFrame(rafRef.current)
    }
  }, [])

  useLayoutEffect(() => {
    recordHistoryEntry(location, navigationType)

    const previousPath = activePathRef.current
    activeKeyRef.current = location.key
    activePathRef.current = location.pathname

    if (navigationType === 'REPLACE' && location.pathname === previousPath) {
      // Same route, new key: keep the viewport where it is. A restore that is
      // still running simply continues and records under the new key when it
      // finishes; otherwise remember the current offset under it now.
      if (!restoringRef.current) {
        scrollPositions.set(location.key, window.scrollY)
      }
      return
    }

    cancelAnimationFrame(rafRef.current)
    restoringRef.current = true

    const target = navigationType === 'POP'
      ? scrollPositions.get(location.key) ?? 0
      : 0

    let frames = 0

    const apply = () => {
      window.scrollTo(0, target)
      frames += 1

      // Page content is frequently loaded async (TanStack Query), so the
      // document may still be growing to its full height. Keep nudging until we
      // reach the target or ~1s of frames has passed, then stop suppressing.
      if (window.scrollY < target && frames < 60) {
        rafRef.current = requestAnimationFrame(apply)
      }
      else {
        restoringRef.current = false
        scrollPositions.set(activeKeyRef.current, window.scrollY)
      }
    }

    apply()
  }, [location.key, location.pathname, navigationType])
}
